import axios from 'axios';
import { wrapAxios } from '../../axios';
import { IPlanChangeResult, ISubscription, IUsageStats, StaticPlanTier } from '@cuspar/ywt.ch.model';

export interface IDowngradeValidation {
  valid: boolean;
  errors: string[];
}

export interface IScheduleDowngradeResult {
  success: boolean;
  errors?: string[];
  subscription?: ISubscription;
}

export interface ICreateUpgradeOrderResult {
  success: boolean;
  orderId?: string;
  error?: string;
}

export interface ICreateRenewalOrderResult {
  success: boolean;
  orderId?: string;
  error?: string;
}

export const subscriptionAPI = {
  /**
   * Get a specific subscription by ID
   */
  getSubscription: async (subscriptionId: string) => {
    return wrapAxios(() => axios.get<ISubscription>(`/subscriptions/${encodeURIComponent(subscriptionId)}`));
  },

  /**
   * Get usage stats for the user's static hosting plan
   */
  getUsageStats: async () => {
    return wrapAxios(() => axios.get<IUsageStats>('/subscriptions/usage/static'));
  },

  /**
   * Calculate plan change - returns whether it's an upgrade or downgrade with proration info
   */
  calculatePlanChange: async (subscriptionId: string, newTier: StaticPlanTier) => {
    return wrapAxios(() => axios.post<IPlanChangeResult>(`/subscriptions/${encodeURIComponent(subscriptionId)}/calculate-change`, { newTier }));
  },

  /**
   * Validate if downgrade is possible based on current usage
   */
  validateDowngrade: async (subscriptionId: string, newTier: StaticPlanTier) => {
    return wrapAxios(() => axios.post<IDowngradeValidation>(`/subscriptions/${encodeURIComponent(subscriptionId)}/validate-downgrade`, { newTier }));
  },

  /**
   * Create an upgrade order - user will navigate to order page to complete payment
   */
  createUpgradeOrder: async (subscriptionId: string, newTier: StaticPlanTier) => {
    return wrapAxios(() => axios.post<ICreateUpgradeOrderResult>(`/subscriptions/${encodeURIComponent(subscriptionId)}/create-upgrade-order`, { newTier }));
  },

  /**
   * Schedule a downgrade for end of billing period
   */
  scheduleDowngrade: async (subscriptionId: string, newTier: StaticPlanTier) => {
    return wrapAxios(() => axios.post<IScheduleDowngradeResult>(`/subscriptions/${encodeURIComponent(subscriptionId)}/schedule-downgrade`, { newTier }));
  },

  /**
   * Cancel a scheduled plan change
   */
  cancelScheduledChange: async (subscriptionId: string) => {
    return wrapAxios(() => axios.delete<ISubscription>(`/subscriptions/${encodeURIComponent(subscriptionId)}/scheduled-change`));
  },

  /**
   * Cancel subscription (will expire at end of billing period)
   */
  cancelSubscription: async (subscriptionId: string) => {
    return wrapAxios(() => axios.post<ISubscription>(`/subscriptions/${encodeURIComponent(subscriptionId)}/cancel`));
  },

  /**
   * Resume a cancelled subscription before its billing period ends
   */
  resumeSubscription: async (subscriptionId: string) => {
    return wrapAxios(() => axios.post<ISubscription>(`/subscriptions/${encodeURIComponent(subscriptionId)}/resume`));
  },

  /**
   * Get the latest static site subscription (active or expired)
   */
  getLatestStaticSiteSubscription: async () => {
    return wrapAxios(() => axios.get<ISubscription | null>(`/subscriptions/latest-static`));
  },

  /**
   * Create a renewal order for an expired subscription
   */
  createRenewalOrder: async (subscriptionId: string) => {
    return wrapAxios(() => axios.post<ICreateRenewalOrderResult>(`/subscriptions/${encodeURIComponent(subscriptionId)}/create-renewal-order`));
  },
};
